/**
 * Market screen model (docs/market-ux.md) — pure and side-effect-free, unit-tested in
 * marketView.test.ts.
 *
 *   links       `/market?c=<contestId>&m=<marketKey>&b=<bookKey>` (or `&h=<positionHash>`, resolved
 *               to its market and line once the game's book loads)
 *   steppers    the header's ‹ ›: the other lines of the same market (and participant), and on a
 *               per-participant market the other players, each on their main line
 *   sides       the book's two sides (buildBookGrid's column order: home|over|yes first)
 *   ladder      GET /depth levels for one side merged across venues: price · total size ·
 *               venues, cheapest first, with the running total (the depth curve)
 *   history     GET /contests/:id/price-history points → one line per venue, which venue was
 *               best at each moment (carried forward: the feed only sends changes), and the band
 *               between the cheapest and dearest venue (max − min)
 *   you         your filled contracts per side from order rows, the payout if each side wins, and
 *               "if settled now" (contracts × best price now − cost, an estimate)
 *   venues      one row per venue: best price per side, size, fees ≈, limit support, connected
 */
import type { PartnerLike, PositionLike } from "./books.js";
import { buildBookGrid } from "./books.js";
import { isActionable } from "./marketsBoard.js";
import { bookParticipantId, isResting, marketParticipants, participantLines, type MmBook, type MmLeg, type MmMarket } from "./marketMaking.js";
import { orderFill, positionHashOf, type OrderLike } from "./portfolio.js";

/* ====================================================================== links */

export interface MarketTarget {
  contestId: string;
  marketKey?: string | null;
  bookKey?: string | null;
  /** Any side of the book; resolved to market + line on the Market screen. */
  positionHash?: string | null;
}

/** The Market screen's deep link for a market (and line), or for the book a position is on. */
export function marketHref(t: MarketTarget): string {
  const qs = new URLSearchParams({ c: t.contestId });
  if (t.marketKey) qs.set("m", t.marketKey);
  if (t.bookKey) qs.set("b", t.bookKey);
  if (!t.marketKey && t.positionHash) qs.set("h", t.positionHash);
  return `/market?${qs.toString()}`;
}

/** The deep link's target, or null without a contest. */
export function parseMarketParams(qs: URLSearchParams): MarketTarget | null {
  const c = qs.get("c");
  if (!c) return null;
  return { contestId: c, marketKey: qs.get("m"), bookKey: qs.get("b"), positionHash: qs.get("h") };
}

/** The market and book a link points at, by keys or by any side's position_hash. */
export function findBook(markets: MmMarket[], t: Omit<MarketTarget, "contestId">): { market: MmMarket; book: MmBook } | null {
  if (t.marketKey) {
    const market = markets.find((m) => m.marketKey === t.marketKey);
    if (!market) return null;
    const book = market.books.find((b) => b.key === (t.bookKey ?? market.defaultBook)) ?? (t.bookKey ? null : market.books[0]);
    return book ? { market, book } : null;
  }
  if (t.positionHash) {
    for (const market of markets) for (const book of market.books) if (book.positions.some((p) => p.position_hash === t.positionHash)) return { market, book };
  }
  return null;
}

/* =================================================================== steppers */

/**
 * The lines the header's ‹ › walks: this market's books for the same participant (props /
 * per-participant 3-way) or the whole market (spreads, totals), lowest line first.
 */
export function siblingLines(market: MmMarket, book: MmBook): MmBook[] {
  const pid = bookParticipantId(book.key);
  const same = market.books.filter((b) => bookParticipantId(b.key) === pid);
  return same
    .map((b, i) => ({ b, i }))
    .sort((x, y) => (x.b.line ?? Infinity) - (y.b.line ?? Infinity) || x.i - y.i)
    .map((x) => x.b);
}

export interface SiblingPlayer {
  id: string;
  name: string;
  /** The book a step opens: the player's main line. */
  book: MmBook;
}

/**
 * The players the header's ‹ › walks on a per-participant market (most venues first, the
 * Market making picker's order), each opening on its main line. Empty on other markets.
 */
export function siblingPlayers(market: MmMarket): SiblingPlayer[] {
  return marketParticipants(market)
    .map((p) => ({ id: p.id, name: p.name, book: participantLines(market, p.id)[0]?.book }))
    .filter((p): p is SiblingPlayer => !!p.book);
}

/* ====================================================================== sides */

export interface MarketSide {
  side: string;
  label: string;
  positionHash: string;
  position: PositionLike;
  consensus: number | null;
}

/** The book's two sides in column order (home|over|yes first). */
export function marketSides(book: MmBook): MarketSide[] {
  return buildBookGrid(book.positions).columns.map((c) => ({
    side: c.side,
    label: c.label,
    positionHash: c.position.position_hash,
    position: c.position,
    consensus: c.consensusPrice,
  }));
}

/* ===================================================================== ladder */

export interface DepthPartnerLike {
  partner_id: string;
  partner_name?: string | null;
  levels?: { price: number; available?: number | null }[] | null;
}

export interface LadderVenue {
  id: string;
  name: string;
  size: number;
}

export interface LadderLevel {
  price: number;
  /** Contracts / size on offer at this price, every venue in scope. */
  size: number;
  /** Running total from the best price down to here. */
  cum: number;
  /** Biggest first. */
  venues: LadderVenue[];
}

/** Prices within this of each other are one level (venues quote 0.68253968… and 0.6825). */
const PRICE_KEY = (p: number) => Math.round(p * 10_000) / 10_000;

/**
 * One side's depth merged across venues, cheapest first (the best price to buy on top), with the
 * running total. `allowed` (Real: connected venues) and `only` (the venue pills) both narrow it;
 * null = no narrowing.
 */
export function ladderLevels(partners: DepthPartnerLike[], allowed: ReadonlySet<string> | null = null, only: ReadonlySet<string> | null = null): LadderLevel[] {
  const by = new Map<number, Map<string, LadderVenue>>();
  for (const p of partners) {
    if (allowed && !allowed.has(p.partner_id)) continue;
    if (only && !only.has(p.partner_id)) continue;
    for (const l of p.levels ?? []) {
      const size = l.available ?? 0;
      if (typeof l.price !== "number" || !(l.price > 0) || !(l.price < 1) || !(size > 0)) continue;
      const k = PRICE_KEY(l.price);
      let lv = by.get(k);
      if (!lv) by.set(k, (lv = new Map()));
      const v = lv.get(p.partner_id);
      if (v) v.size += size;
      else lv.set(p.partner_id, { id: p.partner_id, name: p.partner_name ?? p.partner_id, size });
    }
  }
  let cum = 0;
  return [...by.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([price, vs]) => {
      const venues = [...vs.values()].sort((a, b) => b.size - a.size || a.name.localeCompare(b.name));
      const size = venues.reduce((n, v) => n + v.size, 0);
      cum += size;
      return { price, size, cum, venues };
    });
}

/** The cumulative size vs price curve ("mountain") of a ladder: one point per level, cheapest first. */
export function depthCurve(levels: LadderLevel[]): { price: number; cum: number }[] {
  return levels.map((l) => ({ price: l.price, cum: l.cum }));
}

/**
 * The depth feed has been seen returning the OPPOSITE side's book (FRICTION.md): its best is far
 * from this side's top of book in liquidity but close to the complement.
 */
export function depthLooksFlipped(depthBest: number | null, liquidityBest: number | null): boolean {
  if (depthBest == null || liquidityBest == null) return false;
  return Math.abs(depthBest - liquidityBest) > 0.05 && Math.abs(1 - depthBest - liquidityBest) <= 0.05;
}

export interface VenueQuote {
  id: string;
  name: string;
  price: number;
  american: string | null;
  available: number;
}

/** Each venue's best price on a side from the liquidity feed (the locked-depth fallback), cheapest first. */
export function venueQuotes(pos: PositionLike | undefined, allowed: ReadonlySet<string> | null = null): VenueQuote[] {
  return (pos?.partner_liquidities ?? [])
    .filter((pl) => isActionable(pl) && (!allowed || allowed.has(pl.partner_id)))
    .map((pl) => ({ id: pl.partner_id, name: pl.partner_name ?? pl.partner_id, price: pl.price as number, american: pl.american_odds ?? null, available: pl.available ?? 0 }))
    .sort((a, b) => a.price - b.price || b.available - a.available);
}

/** How far a price is from fair (consensus), in cents: positive = dearer than fair. */
export function gapToFair(price: number | null, fair: number | null): number | null {
  if (price == null || fair == null || !(fair > 0)) return null;
  return Math.round((price - fair) * 1000) / 10;
}

/* ==================================================================== history */

export interface HistoryPointLike {
  timestamp: number;
  position_hash?: string | null;
  partner_id?: string | null;
  partner_name?: string | null;
  price?: number | null;
  available?: number | null;
}

export interface VenueLine {
  id: string;
  name: string;
  points: { t: number; v: number }[];
}

const okPrice = (p: unknown): p is number => typeof p === "number" && p > 0 && p < 1;

/** One line per venue for one side: its price (or its available size) over time, by venue name. */
export function venueLines(points: HistoryPointLike[], hash: string, field: "price" | "available", allowed: ReadonlySet<string> | null = null): VenueLine[] {
  const by = new Map<string, VenueLine>();
  for (const pt of points) {
    if (pt.position_hash && pt.position_hash !== hash) continue;
    const id = pt.partner_id ?? pt.partner_name;
    if (!id || (allowed && !allowed.has(id))) continue;
    if (!okPrice(pt.price)) continue; // a venue with no price has no size at best either
    const v = field === "price" ? pt.price : pt.available;
    if (typeof v !== "number" || !Number.isFinite(v) || v < 0) continue;
    let line = by.get(id);
    if (!line) by.set(id, (line = { id, name: pt.partner_name ?? id, points: [] }));
    line.points.push({ t: pt.timestamp, v });
  }
  for (const l of by.values()) {
    l.points.sort((a, b) => a.t - b.t);
    // One value per timestamp (the last one sent).
    l.points = l.points.filter((p, i, a) => i === a.length - 1 || a[i + 1].t !== p.t);
  }
  return [...by.values()].sort((a, b) => a.name.localeCompare(b.name));
}

export interface TimelineStep {
  t: number;
  /** Every venue's latest price at t (carried forward from its last point). */
  prices: { id: string; name: string; price: number }[];
}

/**
 * Every venue's price at each moment any venue changed (the feed sends a venue only when it
 * moves, so each price holds until its next point).
 */
export function venueTimeline(points: HistoryPointLike[], hash: string, allowed: ReadonlySet<string> | null = null): TimelineStep[] {
  const lines = venueLines(points, hash, "price", allowed);
  const times = [...new Set(lines.flatMap((l) => l.points.map((p) => p.t)))].sort((a, b) => a - b);
  const idx = new Map(lines.map((l) => [l.id, -1]));
  const out: TimelineStep[] = [];
  for (const t of times) {
    const prices: TimelineStep["prices"] = [];
    for (const l of lines) {
      let i = idx.get(l.id)!;
      while (i + 1 < l.points.length && l.points[i + 1].t <= t) i++;
      idx.set(l.id, i);
      if (i >= 0) prices.push({ id: l.id, name: l.name, price: l.points[i].v });
    }
    if (prices.length) out.push({ t, prices });
  }
  return out;
}

export interface BestStep {
  t: number;
  id: string;
  name: string;
  price: number;
}

/** Which venue had the best (lowest) price at each step; ties keep the previous leader. */
export function bestVenueOverTime(timeline: TimelineStep[]): BestStep[] {
  const out: BestStep[] = [];
  let prev: string | null = null;
  for (const s of timeline) {
    let best = s.prices[0];
    for (const p of s.prices) if (p.price < best.price - 1e-9 || (Math.abs(p.price - best.price) <= 1e-9 && p.id === prev)) best = p;
    prev = best.id;
    out.push({ t: s.t, id: best.id, name: best.name, price: best.price });
  }
  return out;
}

/** How far apart the venues were at each step (max − min), where at least two quoted. */
export function spreadBand(timeline: TimelineStep[]): { t: number; lo: number; hi: number }[] {
  return timeline
    .filter((s) => s.prices.length >= 2)
    .map((s) => {
      const ps = s.prices.map((p) => p.price);
      return { t: s.t, lo: Math.min(...ps), hi: Math.max(...ps) };
    });
}

/** The chart ranges, shared by every tab. "All" asks for 7 days hourly (a 1d resolution returns nothing — FRICTION.md). */
export const RANGES = [
  { key: "1h", label: "1h", ms: 3_600_000, resolution: "1m", limit: 1500 },
  { key: "6h", label: "6h", ms: 6 * 3_600_000, resolution: "5m", limit: 1500 },
  { key: "1d", label: "1d", ms: 24 * 3_600_000, resolution: "15m", limit: 2000 },
  { key: "all", label: "All", ms: 7 * 24 * 3_600_000, resolution: "1h", limit: 5000 },
] as const;
export type RangeKey = (typeof RANGES)[number]["key"];

/* ======================================================================== you */

export interface SideHolding {
  contracts: number;
  cost: number;
  /** cost ÷ contracts (null with nothing held). */
  avg: number | null;
}

export interface MarketHolding {
  sides: SideHolding[];
  cost: number;
  /** Net result if each side wins: that side's contracts (each pays 1) − everything paid. */
  ifWins: number[];
  /** Your fills: time × price, per side. */
  fills: { t: number; price: number; contracts: number; col: number }[];
  /** Your resting orders on this book. */
  resting: OrderLike[];
}

/** Your open (unsettled) orders on these sides → contracts, cost, fills and resting orders. */
export function marketHolding(orders: OrderLike[], hashes: string[]): MarketHolding {
  const sides: SideHolding[] = hashes.map(() => ({ contracts: 0, cost: 0, avg: null }));
  const fills: MarketHolding["fills"] = [];
  const resting: OrderLike[] = [];
  for (const o of orders) {
    const col = hashes.indexOf(positionHashOf(o) ?? "");
    if (col < 0 || o.result) continue;
    const f = orderFill(o);
    if (f.contracts > 0) {
      sides[col].contracts += f.contracts;
      sides[col].cost += f.stake;
      const t = Date.parse(o.placed_datetime ?? o.created_at ?? "");
      if (Number.isFinite(t) && typeof o.price === "number") fills.push({ t, price: o.price, contracts: f.contracts, col });
    }
    if (isResting(o as MmLeg)) resting.push(o);
  }
  for (const s of sides) s.avg = s.contracts > 0 ? s.cost / s.contracts : null;
  const cost = sides.reduce((n, s) => n + s.cost, 0);
  return { sides, cost, ifWins: sides.map((s) => s.contracts - cost), fills: fills.sort((a, b) => a.t - b.t), resting };
}

/**
 * "If settled now" (an estimate): what the contracts are worth at each side's best price now,
 * minus their cost. Null when nothing is held or a held side has no price.
 */
export function ifSettledNow(h: Pick<MarketHolding, "sides" | "cost">, best: (number | null)[]): { value: number; pnl: number } | null {
  if (!h.sides.some((s) => s.contracts > 0)) return null;
  let value = 0;
  for (let i = 0; i < h.sides.length; i++) {
    if (h.sides[i].contracts <= 0) continue;
    const p = best[i];
    if (p == null) return null;
    value += h.sides[i].contracts * p;
  }
  return { value, pnl: value - h.cost };
}

/* ===================================================================== venues */

export interface VenueCatalogEntry {
  /** null = unknown (the API refuses when it doesn't). */
  limit: boolean | null;
}

export interface VenueRow {
  id: string;
  name: string;
  prices: (number | null)[];
  american: (string | null)[];
  sizes: (number | null)[];
  /** Fee rates (fraction of the stake, ≈) from fee_packet: the selected side's, else the other's. */
  maker: number | null;
  taker: number | null;
  limit: boolean | null;
  /** Real: connected or not. Paper: null (every venue). */
  connected: boolean | null;
  /** Is this venue's price the best on each side. */
  best: boolean[];
}

const feeRate = (f: unknown): number | null => (typeof f === "number" && Number.isFinite(f) && f >= 0 && f < 100 ? f / 100 : null);

/**
 * One row per venue quoting this book, sorted by best (lowest) price on side `col` (venues with
 * no price there last). `connected` (Real) marks each row; it doesn't filter.
 */
export function venueRows(
  book: MmBook,
  col: number,
  catalog: ReadonlyMap<string, VenueCatalogEntry> | null,
  connected: ReadonlySet<string> | null,
): VenueRow[] {
  const grid = buildBookGrid(book.positions);
  const n = grid.columns.length;
  const rows: VenueRow[] = grid.rows.map((r) => {
    const live = r.cells.map((c) => (isActionable(c) ? (c as PartnerLike) : undefined));
    const pick = (live[col] ?? live.find(Boolean)) as (PartnerLike & { fee_packet?: { maker_fee?: number | null; taker_fee?: number | null } }) | undefined;
    return {
      id: r.partnerId,
      name: r.partnerName,
      prices: live.map((c) => (c ? (c.price as number) : null)),
      american: live.map((c) => c?.american_odds ?? null),
      sizes: live.map((c) => (c ? (c.available ?? null) : null)),
      maker: feeRate(pick?.fee_packet?.maker_fee),
      taker: feeRate(pick?.fee_packet?.taker_fee),
      limit: catalog?.get(r.partnerId)?.limit ?? null,
      connected: connected ? connected.has(r.partnerId) : null,
      best: Array.from({ length: n }, () => false),
    };
  });
  for (let i = 0; i < n; i++) {
    const ps = rows.map((r) => r.prices[i]).filter((p): p is number => p != null);
    if (!ps.length) continue;
    const min = Math.min(...ps);
    for (const r of rows) r.best[i] = r.prices[i] != null && Math.abs((r.prices[i] as number) - min) < 1e-9;
  }
  const at = (r: VenueRow) => r.prices[col] ?? Infinity;
  return rows.filter((r) => r.prices.some((p) => p != null)).sort((a, b) => at(a) - at(b) || a.name.localeCompare(b.name));
}
